import React, { useState } from 'react';
import { ChevronDownIcon, ArrowRightIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
export function HomeTopFAQs() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const faqs = [{
    question: "If your system is so good, why don't you just use it to build your own massive sales team?",
    answer: <>
          <p className="mb-3" data-id="element-497">
            We did. For nearly 20 years. We used this exact approach to recruit
            over 4,000 reps and generate $150M+ in revenue for our own
            organizations ($130M of that in Solar alone).
          </p>
          <p className="mb-3" data-id="element-498">
            So why sell it? We realized that in D2D, the biggest bottleneck is
            trying to wear every hat at once—Knocking, Managing, Training, and
            Recruiting. Even though we were successful, doing "everything" kept
            us from fully unleashing the one thing we loved most: The People
            Side.
          </p>
          <p className="mb-3" data-id="element-499">
            Our true passion is the recruiting journey—giving a rookie a shot,
            watching them succeed, and empowering leaders to build their dream
            teams. We decided to stop being "Generalist Operators" and become
            Specialized Partners. We build the world-class engine so you can
            focus on driving the car.
          </p>
        </>
  }, {
    question: 'How are you different from a recruiter?',
    answer: <>
          <p className="mb-3" data-id="element-500">
            Traditional recruiters focus on filling seats. We help you build a
            structured recruiting, training, and early-retention system that
            keeps producing long after the initial launch. Instead of just
            sending resumes, we support:
          </p>
          <ul className="space-y-2 mb-3 ml-4" data-id="element-501">
            <li data-id="element-502">
              <strong data-id="element-503">Messaging and Positioning:</strong> How you present the
              opportunity.
            </li>
            <li data-id="element-504">
              <strong data-id="element-505">Applicant Generation:</strong> Sourcing the right people.
            </li>
            <li data-id="element-506">
              <strong data-id="element-507">Screening and Qualification:</strong> Filtering out the
              noise.
            </li>
            <li data-id="element-508">
              <strong data-id="element-509">Training & Sales School:</strong> Providing the initial
              kick-start to get them field-ready.
            </li>
            <li data-id="element-510">
              <strong data-id="element-511">Onboarding & Early-Retention:</strong> Systems to keep
              them past the first 60 days.
            </li>
            <li data-id="element-512">
              <strong data-id="element-513">Optimization:</strong> Ongoing refinement based on data.
            </li>
          </ul>
          <p data-id="element-514">
            The goal isn't just "more interviews"—it's a predictable system you
            can rely on month after month.
          </p>
        </>
  }, {
    question: 'How fast will we see results?',
    answer: <>
          <p className="mb-3" data-id="element-515">
            Most companies begin hiring within 3–5 days of launch. Depending on
            your team size and market, you can expect:
          </p>
          <ul className="space-y-2 ml-4" data-id="element-516">
            <li data-id="element-517">
              <strong data-id="element-518">Month 1:</strong> 10–20 new reps.
            </li>
            <li data-id="element-519">
              <strong data-id="element-520">Days 30–60:</strong> Higher rookie stick rates and
              consistent applicant flow.
            </li>
            <li data-id="element-521">
              <strong data-id="element-522">Days 60–90:</strong> A fully dialed-in system (your team +
              our framework working seamlessly).
            </li>
            <li data-id="element-523">
              <strong data-id="element-524">Month 4–5:</strong> Most partners see the system become
              self-funding.
            </li>
          </ul>
        </>
  }, {
    question: 'Do you only work with D2D companies?',
    answer: <>
          <p className="mb-3" data-id="element-525">Yes. We specialize in three specific models:</p>
          <ul className="space-y-2 mb-3 ml-4" data-id="element-526">
            <li data-id="element-527">Door-to-door organizations.</li>
            <li data-id="element-528">Direct sales companies (in-home closing).</li>
            <li data-id="element-529">Field-based home services sales teams.</li>
          </ul>
          <p data-id="element-530">
            This includes Solar, Roofing, Pest, Windows, Telecom, HVAC, Energy,
            Life Insurance, Community Solar, and similar models. Our system is
            built specifically for the unique demands of field
            sales—seasonality, churn patterns, territory structure, and
            aggressive ramp-up cycles.
          </p>
        </>
  }, {
    question: 'What kind of commitment is required?',
    answer: 'We launch with a 90-day commitment. This ensures the recruiting system is installed correctly, your team is aligned, and the early retention systems are fully operational. After that, our service moves to month-to-month, as long as the partnership makes sense for both sides.'
  }, {
    question: 'Do you offer any guarantees?',
    answer: <>
          <p className="mb-3" data-id="element-531">
            Yes. We offer two specific guarantees to protect your investment:
          </p>
          <ul className="space-y-2 ml-4" data-id="element-532">
            <li data-id="element-533">
              <strong data-id="element-534">30-Day Money Back Guarantee:</strong> If you feel the
              system isn't moving in the right direction within the first 30
              days, you may request a full refund.
            </li>
            <li data-id="element-535">
              <strong data-id="element-536">90-Day Performance Guarantee:</strong> If the system is
              not performing in line with the agreed-upon baseline metrics by
              day 90, we continue working for free until it does.
            </li>
          </ul>
        </>
  }, {
    question: 'What kind of involvement is required from our leadership team?',
    answer: <>
          <p className="mb-3" data-id="element-537">
            We handle the majority of the operational and strategic workload,
            but your leadership plays a critical role in:
          </p>
          <ul className="space-y-2 mb-3 ml-4" data-id="element-538">
            <li data-id="element-539">Setting expectations.</li>
            <li data-id="element-540">Providing clear role standards.</li>
            <li data-id="element-541">Supporting new hires in their first weeks.</li>
            <li data-id="element-542">Giving feedback on candidate quality and fit.</li>
          </ul>
          <p data-id="element-543">
            The system dramatically reduces your recruiting burden, but it still
            relies on partnership and communication.
          </p>
        </>
  }, {
    question: 'Will this work in a competitive or saturated market?',
    answer: <>
          <p className="mb-3" data-id="element-544">
            Yes. Most of our partners operate in competitive regions. The system
            adapts through:
          </p>
          <ul className="space-y-2 mb-3 ml-4" data-id="element-545">
            <li data-id="element-546">Market-specific positioning.</li>
            <li data-id="element-547">Targeted messaging.</li>
            <li data-id="element-548">Screening for alignment.</li>
            <li data-id="element-549">Continuous optimization based on data.</li>
          </ul>
          <p data-id="element-550">
            Market competitiveness influences pace and cost, but structure
            always outperforms randomness.
          </p>
        </>
  }, {
    question: 'What if we already have internal recruiters?',
    answer: <>
          <p className="mb-3" data-id="element-551">
            That is often a benefit. Many partners keep their internal
            recruiters and plug them into the HIRE system. We can:
          </p>
          <ul className="space-y-2 mb-3 ml-4" data-id="element-552">
            <li data-id="element-553">Support them.</li>
            <li data-id="element-554">Train them.</li>
            <li data-id="element-555">Provide structure.</li>
            <li data-id="element-556">Improve consistency.</li>
          </ul>
          <p data-id="element-557">
            We're not replacing people—we're upgrading the system they operate
            within to make their output more predictable.
          </p>
        </>
  }];
  return <section className="section-padding bg-gray-50" data-id="element-558">
      <div className="container-custom" data-id="element-559">
        <motion.div className="max-w-3xl mx-auto" initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.5
      }} data-id="element-560">
          <div className="text-center mb-8 md:mb-10 lg:mb-12" data-id="element-561">
            <h2 className="mb-3 md:mb-4 lg:mb-6" data-id="element-562">Frequently Asked Questions</h2>
          </div>

          <div className="space-y-3 md:space-y-4 mb-6 md:mb-8" data-id="element-563">
            {faqs.map((faq, index) => <motion.div key={index} className="bg-white rounded border-2 border-gray-200 overflow-hidden shadow-md" initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            duration: 0.4,
            delay: index * 0.05
          }} data-id="element-564">
                <button className="w-full px-4 md:px-5 lg:px-6 py-3 md:py-4 lg:py-5 flex items-center justify-between text-left hover:bg-gray-50 transition-colors" onClick={() => setOpenIndex(openIndex === index ? null : index)} data-id="element-565">
                  <h4 className="font-semibold pr-6 md:pr-8" data-id="element-566">{faq.question}</h4>
                  <motion.div animate={{
                rotate: openIndex === index ? 180 : 0
              }} transition={{
                duration: 0.3
              }} data-id="element-567">
                    <ChevronDownIcon className="h-5 w-5 md:h-6 md:w-6 text-mint flex-shrink-0" data-id="element-568" />
                  </motion.div>
                </button>
                <AnimatePresence data-id="element-569">
                  {openIndex === index && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3,
                ease: 'easeInOut'
              }} className="overflow-hidden" data-id="element-570">
                      <div className="px-4 md:px-5 lg:px-6 pb-3 md:pb-4 lg:pb-5 bg-gray-50 border-t border-gray-100" data-id="element-571">
                        <div className="leading-relaxed text-sm md:text-base pt-2" data-id="element-572">
                          {typeof faq.answer === 'string' ? <p data-id="element-573">{faq.answer}</p> : faq.answer}
                        </div>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>)}
          </div>

          <div className="text-center" data-id="element-574">
            <a href="/book-call" className="inline-flex items-center bg-mint hover:bg-mint-dark text-deep-blue font-bold px-6 md:px-8 py-3 md:py-4 rounded transition-colors shadow-lg text-sm md:text-base" data-id="element-575">
              Book Your Strategy Call{' '}
              <ArrowRightIcon className="ml-2 h-4 w-4 md:h-5 md:w-5" data-id="element-576" />
            </a>
          </div>
        </motion.div>
      </div>
    </section>;
}